import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useParams,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Settings,
  Users,
  UserPlus,
  AlertTriangle,
  Loader,
  MoreVertical,
  Shield,
  UserMinus,
  LogOut,
  Key,
  Upload,
  ShieldCheck,
  Download,
} from "lucide-react";
import { motion } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";
import EditWorkspaceModal from "./components/EditWorkspaceModal";
import InviteMemberModal from "./components/InviteMemberModal";
import DeleteWorkspaceModal from "./components/DeleteWorkspaceModal";
import CreateApiKeyModal from "./components/CreateApiKeyModal";
import ApiKeyCreatedModal from "./components/ApiKeyCreatedModal";
import RevokeApiKeyModal from "./components/RevokeApiKeyModal";
import ParentConsentSettings from "./components/ParentConsentSettings";
import {
  consentRecordsCsv,
  consentRecordsFilename,
} from "utils/consentRecordsCsv";
import Modal from "components/common/Modal";
import ConfirmDialog from "./components/common/ConfirmDialog";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import useWorkspaceLogo from "v2/hooks/useWorkspaceLogo";
import {
  ALLOWED_LOGO_TYPES,
  logoFileProblem,
  logoUploadForm,
} from "v2/utils/logoUpload";

// Generate a gradient based on name
const generateGradient = (name) => {
  if (!name) return "linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)";
  const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hue1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 65%, 55%), hsl(${hue2}, 65%, 45%))`;
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

// 3-dot menu for member rows
const MemberMenu = ({ member, onChangeRole, onRemove, onSignOut }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const newRole = member.role === "admin" ? "coach" : "admin";
  const roleLabel =
    member.role === "admin"
      ? t("settings.workspace.memberMenu.changeToCoach")
      : t("settings.workspace.memberMenu.changeToAdmin");

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#6B7280]"
      >
        <MoreVertical size={16} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-xl border border-[#F3F4F6] bg-white py-1 shadow-lg">
          <button
            onClick={() => {
              onChangeRole(member.id, newRole);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            <Shield size={14} className="text-[#6B7280]" />
            {roleLabel}
          </button>
          <button
            onClick={() => {
              onSignOut(member);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            <LogOut size={14} className="text-[#6B7280]" />
            {t("settings.workspace.memberMenu.signOutEverywhere")}
          </button>
          <button
            onClick={() => {
              onRemove(member);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
          >
            <UserMinus size={14} />
            {t("settings.workspace.memberMenu.removeMember")}
          </button>
        </div>
      )}
    </div>
  );
};

const memberDisplayName = (member) =>
  (member?.fname
    ? `${member.fname} ${member.lname || ""}`.trim()
    : member?.email) || "";

const WorkspaceSettings = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { onWorkspaceDeleted, onWorkspaceUpdated } = useOutletContext() || {};

  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);

  // Determine if current user is admin
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentMembership = members.find((m) => m.email === currentUser.email);
  const isAdmin = currentMembership?.role === "admin";
  const isSuperadmin = currentUser.is_superadmin === true;

  // Tab state — driven by ?tab= search param
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = [
    "general",
    "members",
    "invites",
    "api-keys",
    "parent-consent",
    "danger",
  ];
  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : "general";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  // Logo state
  const {
    logoUrl,
    loading: logoLoading,
    refetch: refetchLogo,
  } = useWorkspaceLogo(workspaceId);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [exportingConsent, setExportingConsent] = useState(false);

  const handleExportConsent = async () => {
    setExportingConsent(true);
    try {
      const { data } = await betaAPI.exportConsentRecords({ workspaceId });
      const blob = new Blob([consentRecordsCsv(data.records)], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = consentRecordsFilename(data.workspace_name);
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export consent records", error);
      showToast.error(t("settings.workspace.general.consentExportFailed"));
    } finally {
      setExportingConsent(false);
    }
  };
  const logoInputRef = useRef(null);

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected after an error
    e.target.value = "";

    const problem = logoFileProblem(file);
    if (problem) {
      showToast.error(
        t(
          problem === "size"
            ? "settings.workspace.toasts.logoTooLarge"
            : "settings.workspace.toasts.unsupportedLogoType"
        )
      );
      return;
    }

    setUploadingLogo(true);
    try {
      // Presigned POST: S3 rejects files over 10 MB
      const { data } = await betaAPI.getWorkspaceLogoUploadUrl({
        workspaceId,
        contentType: file.type,
      });
      if (!data?.upload_url) throw new Error("No upload URL returned");
      if (data.max_bytes && file.size > data.max_bytes) {
        showToast.error(t("settings.workspace.toasts.logoTooLarge"));
        return;
      }

      const s3Res = await fetch(data.upload_url, {
        method: "POST",
        body: logoUploadForm(data.fields, file),
      });
      if (!s3Res.ok) throw new Error(`S3 upload failed (${s3Res.status})`);

      await betaAPI.confirmWorkspaceLogo({
        workspaceId,
        contentType: file.type,
      });

      showToast.success(t("settings.workspace.toasts.logoUploaded"));
      refetchLogo();
    } catch (err) {
      console.error("Logo upload failed:", err);
      showToast.error(t("settings.workspace.toasts.logoUploadFailed"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [workspaceRes, membersRes, invitesRes] = await Promise.all([
        betaAPI.getWorkspace({ workspaceId }),
        betaAPI.getMembers({ workspaceId }),
        betaAPI.getInvites({ workspaceId }),
      ]);
      setWorkspace(workspaceRes.data);
      const membersData = membersRes.data;
      setMembers(
        Array.isArray(membersData)
          ? membersData
          : membersData?.results || membersData?.members || []
      );
      const invitesData = invitesRes.data;
      setInvites(
        Array.isArray(invitesData)
          ? invitesData
          : invitesData?.results || invitesData?.invites || []
      );

      // Fetch API keys (may 403 for non-admins)
      try {
        const apiKeysRes = await betaAPI.getApiKeys({
          workspaceId,
          includeRevoked: showRevoked,
        });
        const keysData = apiKeysRes.data;
        setApiKeys(
          Array.isArray(keysData)
            ? keysData
            : keysData?.api_keys || keysData?.results || keysData?.keys || []
        );
      } catch {
        // Non-admins may get 403 — silently ignore
        setApiKeys([]);
      }
    } catch (err) {
      console.error("Failed to load workspace settings:", err);
      showToast.error(t("settings.workspace.toasts.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, showRevoked]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditWorkspace = async ({ name }) => {
    try {
      await betaAPI.updateWorkspace({ workspaceId, body: { name } });
      showToast.success(t("settings.workspace.toasts.workspaceUpdated"));
      setWorkspace((prev) => ({ ...prev, name }));
      onWorkspaceUpdated?.();
    } catch (err) {
      console.error("Failed to update workspace:", err);
      showToast.error(t("settings.workspace.toasts.updateWorkspaceFailed"));
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      const { data } = await betaAPI.updateMember({
        workspaceId,
        memberId,
        body: { role: newRole },
      });
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: data.role } : m))
      );
      showToast.success(t("settings.workspace.toasts.roleUpdated"));
    } catch (err) {
      console.error("Failed to update role:", err);
      if (err.status === 403) {
        showToast.error(t("settings.workspace.toasts.onlyAdminsChangeRoles"));
      } else {
        showToast.error(t("settings.workspace.toasts.updateRoleFailed"));
      }
    }
  };

  const [memberToSignOut, setMemberToSignOut] = useState(null);
  const handleConfirmSignOutMember = async () => {
    const member = memberToSignOut;
    setMemberToSignOut(null);
    if (!member) return;
    try {
      await betaAPI.signOutMemberEverywhere({
        workspaceId,
        memberId: member.id,
      });
      showToast.success(
        t("settings.workspace.toasts.memberSignedOut", {
          name: memberDisplayName(member),
        })
      );
    } catch (err) {
      console.error("Failed to sign member out:", err);
      showToast.error(t("settings.workspace.toasts.memberSignOutFailed"));
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;
    setRemoving(true);
    try {
      await betaAPI.removeMember({ workspaceId, memberId: memberToRemove.id });
      setMembers((prev) => prev.filter((m) => m.id !== memberToRemove.id));
      showToast.success(t("settings.workspace.toasts.memberRemoved"));
      setMemberToRemove(null);
    } catch (err) {
      console.error("Failed to remove member:", err);
      showToast.error(t("settings.workspace.toasts.removeMemberFailed"));
    } finally {
      setRemoving(false);
    }
  };

  const handleSendInvite = async ({ email, role }) => {
    try {
      await betaAPI.sendInvite({ workspaceId, email, role });
      showToast.success(t("settings.workspace.toasts.inviteSent"));
      const invitesRes = await betaAPI.getInvites({ workspaceId });
      const refreshedInvites = invitesRes.data;
      setInvites(
        Array.isArray(refreshedInvites)
          ? refreshedInvites
          : refreshedInvites?.results || refreshedInvites?.invites || []
      );
    } catch (err) {
      console.error("Failed to send invite:", err);
      if (err.status === 409) {
        showToast.error(t("settings.workspace.toasts.alreadyMemberOrInvited"));
      } else {
        showToast.error(t("settings.workspace.toasts.sendInviteFailed"));
      }
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await betaAPI.revokeInvite({ workspaceId, inviteId });
      showToast.success(t("settings.workspace.toasts.inviteRevoked"));
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (err) {
      console.error("Failed to revoke invite:", err);
      showToast.error(t("settings.workspace.toasts.revokeInviteFailed"));
    }
  };

  const handleDeleteWorkspace = async () => {
    try {
      await betaAPI.deleteWorkspace({ workspaceId });
      onWorkspaceDeleted?.();
    } catch (err) {
      console.error("Failed to delete workspace:", err);
      showToast.error(t("settings.workspace.toasts.deleteWorkspaceFailed"));
    }
  };

  const handleLeaveWorkspace = async () => {
    setLeaving(true);
    try {
      await betaAPI.leaveWorkspace({ workspaceId });
      showToast.success(t("settings.workspace.toasts.leftWorkspace"));
      onWorkspaceDeleted?.();
    } catch (err) {
      console.error("Failed to leave workspace:", err);
      if (err.status === 409) {
        showToast.error(t("settings.workspace.toasts.onlyAdminCannotLeave"));
      } else {
        showToast.error(t("settings.workspace.toasts.leaveWorkspaceFailed"));
      }
    } finally {
      setLeaving(false);
      setShowLeaveModal(false);
    }
  };

  const handleCreateApiKey = async (data) => {
    try {
      const res = await betaAPI.createApiKey({ workspaceId, body: data });
      setCreatedKey(res.data);
      showToast.success(t("settings.workspace.toasts.apiKeyCreated"));
      // Refresh keys list
      const apiKeysRes = await betaAPI.getApiKeys({
        workspaceId,
        includeRevoked: showRevoked,
      });
      const keysData = apiKeysRes.data;
      setApiKeys(
        Array.isArray(keysData)
          ? keysData
          : keysData?.api_keys || keysData?.results || keysData?.keys || []
      );
    } catch (err) {
      console.error("Failed to create API key:", err);
      showToast.error(t("settings.workspace.toasts.createApiKeyFailed"));
      throw err;
    }
  };

  const handleRevokeApiKey = async (key) => {
    try {
      await betaAPI.revokeApiKey({ workspaceId, keyId: key.id });
      setApiKeys((prev) => prev.filter((k) => k.id !== key.id));
      showToast.success(t("settings.workspace.toasts.apiKeyRevoked"));
    } catch (err) {
      console.error("Failed to revoke API key:", err);
      showToast.error(t("settings.workspace.toasts.revokeApiKeyFailed"));
      throw err;
    }
  };

  const removeMemberName = memberToRemove
    ? (memberToRemove.fname
        ? `${memberToRemove.fname} ${memberToRemove.lname || ""}`.trim()
        : memberToRemove.email) ||
      t("settings.workspace.removeModal.memberFallback")
    : "";

  if (loading) {
    return (
      <motion.div
        className="min-h-full bg-[#F8F9FA]"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <div className="flex items-center justify-center py-20">
          <Loader size={24} className="animate-spin text-[#E07A3A]" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="min-h-full bg-[#F8F9FA]"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Hero Section with gradient */}
      <div
        style={{
          background:
            "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
        }}
      >
        <div className="mx-auto max-w-2xl px-6 pt-8">
          {/* Back link */}
          <button
            onClick={() => navigate(`/v2/${workspaceId}`)}
            className="mb-4 flex items-center gap-1.5 text-sm text-[#6B7280] transition duration-150 hover:text-[#374151]"
          >
            <ArrowLeft size={16} />
            {t("settings.workspace.backTo", {
              name:
                workspace?.name || t("settings.workspace.workspaceFallback"),
            })}
          </button>

          {/* Page Header */}
          <div>
            <h1 className="text-xl font-bold text-[#111827]">
              {t("settings.workspace.title")}
            </h1>
            <p className="mt-1 text-sm text-[#6B7280]">
              {isAdmin
                ? t("settings.workspace.subtitleAdmin")
                : t("settings.workspace.subtitleMember")}
            </p>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 overflow-x-auto">
            {[
              { id: "general", labelKey: "general", icon: Settings },
              { id: "members", labelKey: "members", icon: Users },
              ...(isAdmin
                ? [
                    { id: "invites", labelKey: "invites", icon: UserPlus },
                    { id: "api-keys", labelKey: "apiKeys", icon: Key },
                  ]
                : []),
              ...(isSuperadmin
                ? [
                    {
                      id: "parent-consent",
                      labelKey: "parentConsent",
                      icon: ShieldCheck,
                    },
                  ]
                : []),
              { id: "danger", labelKey: "danger", icon: AlertTriangle },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              const isDanger = tab.id === "danger";
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="relative flex items-center gap-1.5 rounded-t-lg px-4 py-2.5 text-sm font-medium transition-all duration-150"
                  style={{
                    color: isActive
                      ? isDanger
                        ? "#DC2626"
                        : "#E07A3A"
                      : "#6B7280",
                    background: isActive ? "white" : "transparent",
                  }}
                >
                  <Icon size={15} />
                  {t(`settings.workspace.tabs.${tab.labelKey}`)}
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t"
                      style={{
                        background: isDanger ? "#DC2626" : "#E07A3A",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-6 pb-8">
        {/* Tab Content */}
        <div className="rounded-xl border border-[#F3F4F6] bg-white">
          {/* General Tab */}
          {activeTab === "general" && (
            <div className="divide-y divide-[#F3F4F6]">
              {/* Logo row */}
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                  {/* Logo preview or gradient fallback */}
                  {logoLoading ? (
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F3F4F6]">
                      <Loader
                        size={18}
                        className="animate-spin text-[#9CA3AF]"
                      />
                    </div>
                  ) : logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={t("settings.workspace.general.logoAlt")}
                      className="h-14 w-14 rounded-2xl object-cover shadow-sm"
                    />
                  ) : (
                    <div
                      className="flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-bold text-white shadow-sm"
                      style={{ background: generateGradient(workspace?.name) }}
                    >
                      {getInitials(workspace?.name)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-[#111827]">
                      {t("settings.workspace.general.logoTitle")}
                    </p>
                    <p className="mt-0.5 text-xs text-[#6B7280]">
                      {t("settings.workspace.general.logoFormats")}
                    </p>
                  </div>
                </div>
                {isAdmin && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={ALLOWED_LOGO_TYPES.join(",")}
                      className="hidden"
                      onChange={handleLogoFileChange}
                    />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:cursor-wait disabled:opacity-60"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader size={14} className="animate-spin" />
                          {t("settings.workspace.general.uploading")}
                        </>
                      ) : (
                        <>
                          <Upload size={14} />
                          {t("settings.workspace.general.uploadLogo")}
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>

              {/* Workspace name row */}
              <div className="flex items-center justify-between p-6">
                <div>
                  <p className="text-sm font-medium text-[#111827]">
                    {workspace?.name}
                  </p>
                  <p className="mt-0.5 text-xs text-[#6B7280]">
                    {t("settings.workspace.general.nameLabel")}
                  </p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    {t("settings.workspace.general.editName")}
                  </button>
                )}
              </div>

              {/* Consent records row (admins) */}
              {isAdmin && (
                <div className="flex items-center justify-between gap-4 p-6">
                  <div>
                    <p className="text-sm font-medium text-[#111827]">
                      {t("settings.workspace.general.consentRecords")}
                    </p>
                    <p className="mt-0.5 text-xs text-[#6B7280]">
                      {t("settings.workspace.general.consentRecordsHint")}
                    </p>
                  </div>
                  <button
                    onClick={handleExportConsent}
                    disabled={exportingConsent}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:cursor-wait disabled:opacity-60"
                  >
                    {exportingConsent ? (
                      <Loader size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    {t("settings.workspace.general.consentExport")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === "members" && (
            <div>
              {members.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">
                  {t("settings.workspace.members.empty")}
                </div>
              ) : (
                members.map((member) => {
                  const fname = member.fname || "";
                  const lname = member.lname || "";
                  const email = member.email || "";
                  const displayName = fname
                    ? `${fname} ${lname}`.trim()
                    : email || t("settings.workspace.members.unknown");

                  return (
                    <div
                      key={member.id}
                      className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-3 last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
                          style={{
                            background: generateGradient(fname || email),
                          }}
                        >
                          {getInitials(displayName)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111827]">
                            {displayName}
                          </p>
                          {email && (
                            <p className="text-xs text-[#6B7280]">{email}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-[#E0F2F1] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#00796B]">
                          {t(
                            `settings.workspace.roles.${
                              member.role || "member"
                            }`,
                            { defaultValue: member.role || "member" }
                          )}
                        </span>
                        {isAdmin && (
                          <MemberMenu
                            member={member}
                            onChangeRole={handleUpdateMemberRole}
                            onRemove={(m) => setMemberToRemove(m)}
                            onSignOut={(m) => setMemberToSignOut(m)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Invites Tab */}
          {activeTab === "invites" && isAdmin && (
            <div>
              <div className="flex items-center justify-end border-b border-[#F3F4F6] px-6 py-3">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                >
                  <UserPlus size={14} className="text-[#6B7280]" />
                  {t("settings.workspace.invites.sendInvite")}
                </button>
              </div>
              {invites.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">
                  {t("settings.workspace.invites.empty")}
                </div>
              ) : (
                invites.map((invite) => {
                  const inviteEmail =
                    invite.invitee_email || invite.email || "";

                  return (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-3 last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="text-sm font-medium text-[#111827]">
                            {inviteEmail ||
                              t("settings.workspace.invites.noEmail")}
                          </p>
                          <div className="mt-0.5 flex items-center gap-2">
                            <span className="rounded bg-[#E0F2F1] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#00796B]">
                              {t(
                                `settings.workspace.roles.${
                                  invite.role || "coach"
                                }`,
                                { defaultValue: invite.role || "coach" }
                              )}
                            </span>
                            {(invite.created_at || invite.sent_at) && (
                              <span className="text-[11px] text-[#9CA3AF]">
                                {new Date(
                                  invite.created_at || invite.sent_at
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="rounded-lg border border-[#FECACA] bg-white px-3 py-1.5 text-xs font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
                      >
                        {t("settings.workspace.invites.revoke")}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === "api-keys" && isAdmin && (
            <div>
              <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-3">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-[#6B7280]">
                  <input
                    type="checkbox"
                    checked={showRevoked}
                    onChange={(e) => setShowRevoked(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-[#D1D5DB] accent-[#7C3AED]"
                  />
                  {t("settings.workspace.apiKeys.showRevoked")}
                </label>
                <button
                  onClick={() => setShowCreateKeyModal(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                >
                  <Key size={14} className="text-[#6B7280]" />
                  {t("settings.workspace.apiKeys.createKey")}
                </button>
              </div>
              {apiKeys.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">
                  {t("settings.workspace.apiKeys.empty")}
                </div>
              ) : (
                apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-3 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#111827]">
                            {key.name}
                          </p>
                          {!key.is_active ? (
                            <span className="rounded bg-[#FEF2F2] px-1.5 py-0.5 text-[10px] font-medium text-[#DC2626]">
                              {t("settings.workspace.apiKeys.revoked")}
                            </span>
                          ) : (
                            <span className="rounded bg-[#F0FDF4] px-1.5 py-0.5 text-[10px] font-medium text-[#16A34A]">
                              {t("common.status.active")}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <code className="text-xs text-[#6B7280]">
                            {key.key_prefix}...
                          </code>
                          {key.permissions?.privileges?.map((p) => (
                            <span
                              key={p}
                              className="rounded bg-[#F3E8FF] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#7C3AED]"
                            >
                              {t(`settings.workspace.privileges.${p}`, {
                                defaultValue: p,
                              })}
                            </span>
                          ))}
                          {key.permissions?.resources?.map((r) => (
                            <span
                              key={r}
                              className="rounded bg-[#EEF2FF] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#6366F1]"
                            >
                              {t(`settings.workspace.resources.${r}`, {
                                defaultValue: r,
                              })}
                            </span>
                          ))}
                          {key.created_at && (
                            <span className="text-[11px] text-[#9CA3AF]">
                              {new Date(key.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {key.is_active && (
                      <button
                        onClick={() => setKeyToRevoke(key)}
                        className="rounded-lg border border-[#FECACA] bg-white px-3 py-1.5 text-xs font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
                      >
                        {t("settings.workspace.apiKeys.revoke")}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === "parent-consent" && isSuperadmin && (
            <ParentConsentSettings workspaceId={workspaceId} />
          )}

          {activeTab === "danger" && (
            <div className="p-6">
              {isAdmin ? (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-[#111827]">
                      {t("settings.workspace.danger.deleteTitle")}
                    </h3>
                    <p className="mt-0.5 text-xs text-[#6B7280]">
                      {t("settings.workspace.danger.deleteDescription")}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="rounded-lg border border-[#FECACA] bg-white px-4 py-2 text-sm font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
                  >
                    {t("settings.workspace.danger.deleteTitle")}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-[#111827]">
                      {t("settings.workspace.danger.leaveTitle")}
                    </h3>
                    <p className="mt-0.5 text-xs text-[#6B7280]">
                      {t("settings.workspace.danger.leaveDescription")}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowLeaveModal(true)}
                    className="rounded-lg border border-[#FECACA] bg-white px-4 py-2 text-sm font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
                  >
                    {t("settings.workspace.danger.leaveTitle")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <EditWorkspaceModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditWorkspace}
        workspace={workspace}
      />

      <InviteMemberModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleSendInvite}
        workspaceName={workspace?.name}
      />

      <DeleteWorkspaceModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteWorkspace}
        workspace={workspace}
      />

      {/* Leave Workspace Confirmation Modal */}
      <Modal
        show={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        maxWidth="420px"
      >
        <div
          className="relative overflow-hidden p-7"
          style={{
            background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
          }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: -30,
              right: -30,
              background: "rgba(255, 255, 255, 0.1)",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              bottom: -40,
              right: 60,
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />
          <div className="relative">
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <LogOut size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {t("settings.workspace.danger.leaveTitle")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("settings.workspace.leaveModal.subtitle")}
            </p>
          </div>
        </div>

        <div className="p-7">
          <p className="text-sm text-[#374151]">
            <Trans
              i18nKey="settings.workspace.leaveModal.body"
              values={{ name: workspace?.name }}
              components={{
                bold: <span className="font-semibold text-[#111827]" />,
              }}
            />
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
          <button
            onClick={() => setShowLeaveModal(false)}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={handleLeaveWorkspace}
            disabled={leaving}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={{
              background: "#DC2626",
              boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
              cursor: leaving ? "wait" : "pointer",
            }}
          >
            {leaving ? (
              <span className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                {t("settings.workspace.leaveModal.leaving")}
              </span>
            ) : (
              t("settings.workspace.leaveModal.confirm")
            )}
          </button>
        </div>
      </Modal>

      {/* API Key Modals */}
      <CreateApiKeyModal
        show={showCreateKeyModal}
        onClose={() => setShowCreateKeyModal(false)}
        onCreate={handleCreateApiKey}
      />

      <ApiKeyCreatedModal
        show={!!createdKey}
        onClose={() => setCreatedKey(null)}
        apiKey={createdKey}
      />

      <RevokeApiKeyModal
        show={!!keyToRevoke}
        onClose={() => setKeyToRevoke(null)}
        onRevoke={handleRevokeApiKey}
        apiKey={keyToRevoke}
      />

      {/* Remove Member Confirmation Modal */}
      <ConfirmDialog
        open={!!memberToSignOut}
        title={t("settings.workspace.signOutModal.title", {
          name: memberDisplayName(memberToSignOut),
        })}
        body={t("settings.workspace.signOutModal.body")}
        confirmLabel={t("settings.workspace.signOutModal.confirm")}
        danger
        onConfirm={handleConfirmSignOutMember}
        onCancel={() => setMemberToSignOut(null)}
      />

      <Modal
        show={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        maxWidth="420px"
      >
        <div
          className="relative overflow-hidden p-7"
          style={{
            background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
          }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: -30,
              right: -30,
              background: "rgba(255, 255, 255, 0.1)",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              bottom: -40,
              right: 60,
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />
          <div className="relative">
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <UserMinus size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {t("settings.workspace.removeModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("settings.workspace.leaveModal.subtitle")}
            </p>
          </div>
        </div>

        <div className="p-7">
          <p className="text-sm text-[#374151]">
            <Trans
              i18nKey="settings.workspace.removeModal.body"
              values={{ name: removeMemberName }}
              components={{
                bold: <span className="font-semibold text-[#111827]" />,
              }}
            />
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
          <button
            onClick={() => setMemberToRemove(null)}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={handleConfirmRemoveMember}
            disabled={removing}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={{
              background: "#DC2626",
              boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
              cursor: removing ? "wait" : "pointer",
            }}
          >
            {removing ? (
              <span className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                {t("settings.workspace.removeModal.removing")}
              </span>
            ) : (
              t("settings.workspace.removeModal.confirm")
            )}
          </button>
        </div>
      </Modal>
    </motion.div>
  );
};

export default WorkspaceSettings;
